import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = 'RHB Centre, Jalan Tun Razak, 50400 Kuala Lumpur, Malaysia';
  const billingAddress = 'RHB Centre, Jalan Tun Razak, 50400 Kuala Lumpur, Malaysia';
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-MY', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-4921">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-4922">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4923">
          <div className="flex items-center justify-between h-16" data-id="element-4924">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-4925">
              Challenge dispute
            </h1>
            <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-4926">
              <XIcon size={20} data-id="element-4927" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-4928">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-4929">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-4930">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-4931">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-4932">
                <span data-id="element-4933">Challenge details</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-4934" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-4935" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-4936">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-4937">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4938" />}
                    Challenge reason
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4939">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4940" />}
                    Supporting evidence
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-4941">
                <span data-id="element-4942">Transaction details</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-4943" /> : <ChevronDownIcon size={16} data-id="element-4944" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-4945">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4946">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4947" />}
                    Order details
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4948">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4949" />}
                    Customer details
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-4950">
                <span data-id="element-4951">Other evidence</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-4952" /> : <ChevronDownIcon size={16} data-id="element-4953" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-4954">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4955">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4956" />}
                    Other supporting documents
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-4957">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-4958">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4959">
                  <div data-id="element-4960">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4961">
                      Challenge reason
                    </h1>
                    <p className="text-gray-600" data-id="element-4962">
                      Your responses will help us recommend the best evidence to
                      submit for your challenge.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4963">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4964">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4965">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4966">
                      <div className="text-gray-500" data-id="element-4967">Dispute amount</div>
                      <div className="font-medium" data-id="element-4968">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4969">
                      <div className="text-gray-500" data-id="element-4970">Response due</div>
                      <div className="font-medium" data-id="element-4971">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4972">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4973">
                  <div data-id="element-4974">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4975">
                      Why are you challenging the dispute?
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4976">
                      Choose the closest option.
                    </p>
                    <div className="space-y-3" data-id="element-4977">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4978">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4979" />
                        <span className="text-base text-gray-900" data-id="element-4980">
                          The customer withdrew the dispute
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4981">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4982" />
                        <span className="text-base text-gray-900" data-id="element-4983">
                          The customer has already been refunded
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4984">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4985" />
                        <span className="text-base text-gray-900" data-id="element-4986">
                          The customer has a purchasing history with me
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4987">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4988" />
                        <span className="text-base text-gray-900" data-id="element-4989">
                          Other reasons
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-4990">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4991">
                      What type of product/service was the disputed payment for?
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4992">
                      Choose the closest option.
                    </p>
                    <div className="space-y-3" data-id="element-4993">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4994">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4995" />
                        <div className="flex-1" data-id="element-4996">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4997">
                            Physical product
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4998">
                            Physical goods to be delivered to the customer, e.g.
                            clothes, accessories, electronic gadgets
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4999">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5000" />
                        <div className="flex-1" data-id="element-5001">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5002">
                            Digital product or service
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5003">
                            Purchased access to software, digital files or
                            experiences, e.g. mobile apps, music service,
                            digital tickets
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5004">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5005" />
                        <div className="flex-1" data-id="element-5006">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5007">
                            Offline service
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5008">
                            Labour or assistant services provided either at a
                            specific location or remotely, e.g. transportation,
                            home servicing
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5009">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5010" />
                        <div className="flex-1" data-id="element-5011">
                          <div className="text-base text-gray-900 font-medium" data-id="element-5012">
                            Reservation or booking
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-5013">
                            Services related to reservations or bookings, e.g.
                            flight tickets, hotel booking
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-5014">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-5015" />
                        <span className="text-base text-gray-900" data-id="element-5016">Other</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5017">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5018">
                    Cancel
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-5019">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-5020">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5021">
                  <div data-id="element-5022">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5023">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-5024">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-5025">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5026">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5027">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5028">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5029">
                      <div className="text-gray-500" data-id="element-5030">Dispute amount</div>
                      <div className="font-medium" data-id="element-5031">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5032">
                      <div className="text-gray-500" data-id="element-5033">Response due</div>
                      <div className="font-medium" data-id="element-5034">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5035">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5036">
                  <div data-id="element-5037">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5038">
                      Recommended evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5039">
                      Based on your challenge reason, we recommend providing:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-5040">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-5041">
                          <strong data-id="element-5042">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-5043">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5044">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-5045">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-5046" />
                            {requirements.language}
                            <br data-id="element-5047" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5048">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5049">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5050">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5051">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5052" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5053" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5054">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5055">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5056">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5057">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5058">
                              <EyeIcon size={20} data-id="element-5059" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5060">
                              <Trash2Icon size={20} data-id="element-5061" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5062">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5063">
                      <UploadIcon size={16} data-id="element-5064" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-5065">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5066" />
                  </div>

                  <div data-id="element-5067">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5068">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5069" />
                      <span className="text-sm text-gray-900" data-id="element-5070">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5071">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5072">
                    Back
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5073">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-5074">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5075">
                  <div data-id="element-5076">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5077">
                      Order details
                    </h1>
                    <p className="text-gray-600" data-id="element-5078">
                      Please provide as much information as possible about the
                      goods/service the payment was disputed for. This will
                      allow the issuer to verify that the evidence you provided
                      corresponds to the transaction disputed by the customer.
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5079">
                      The details below have been pre-filled from transaction
                      data. Please review and edit the information if needed.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5080">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5081">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5082">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5083">
                      <div className="text-gray-500" data-id="element-5084">Dispute amount</div>
                      <div className="font-medium" data-id="element-5085">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5086">
                      <div className="text-gray-500" data-id="element-5087">Response due</div>
                      <div className="font-medium" data-id="element-5088">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5089">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5090">
                  <div data-id="element-5091">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5092">
                      Product details
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5093">
                      <span data-id="element-5094">
                        IPHONE7 * 5,{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5095">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5096">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5097" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5098">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5099">
                      Shipping address
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5100">
                      <span data-id="element-5101">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5102">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5103">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5104" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5105">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5106">
                      Shipping carrier
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5107">
                      <span data-id="element-5108">sameday</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5109">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5110">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5111" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5112">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5113">
                      Provide additional supporting evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5114">
                      As the payment was for a{' '}
                      <span className="font-medium" data-id="element-5115">Physical product</span>, we
                      recommend you to also provide the following information:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-5116">
                      <li data-id="element-5117">Shipping date</li>
                      <li data-id="element-5118">Shipping tracking number</li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5119">
                      You can upload any document containing such information
                      (e.g. invoices, shipping documentation, delivery
                      confirmation or shipping receipts) below.
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5120">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5121" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5122" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-5123">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5124">
                      <UploadIcon size={16} data-id="element-5125" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-5126">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5127" />
                  </div>

                  <div data-id="element-5128">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5129">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5130" />
                      <span className="text-sm text-gray-900" data-id="element-5131">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5132">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5133">
                    Back
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5134">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-5135">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5136">
                  <div data-id="element-5137">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5138">
                      Customer details
                    </h1>
                    <p className="text-gray-600" data-id="element-5139">
                      Please provide as much information as possible about the
                      customer. This will allow the issuer to verify that the
                      evidence you provided corresponds to the customer
                      disputing the transaction.
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5140">
                      The details below have been pre-filled from transaction
                      data. Please review and edit the information if needed.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5141">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5142">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5143">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5144">
                      <div className="text-gray-500" data-id="element-5145">Dispute amount</div>
                      <div className="font-medium" data-id="element-5146">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5147">
                      <div className="text-gray-500" data-id="element-5148">Response due</div>
                      <div className="font-medium" data-id="element-5149">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5150">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5151">
                  <div data-id="element-5152">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5153">
                      Customer name
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5154">
                      <span data-id="element-5155">Test name</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5156">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5157">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5158" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5159">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5160">
                      Customer email
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5161">
                      <span data-id="element-5162">customer@example.com</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5163">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5164">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5165" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5166">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5167">
                      Customer purchase IP
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5168">
                      <span data-id="element-5169">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5170">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5171">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5172" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5173">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5174">
                      Customer billing address
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5175">
                      <span data-id="element-5176">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5177">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5178">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5179" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5180">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5181">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5182">
                      We recommend you to provide the following information
                      about the customer:
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5183">
                      You can upload any document containing such information
                      (e.g. invoices, receipts) below.
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5184">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5185" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5186" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5187">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5188">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5189">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5190">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5191" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5192" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5193">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5194">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5195">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5196">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5197">
                              <EyeIcon size={20} data-id="element-5198" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5199">
                              <Trash2Icon size={20} data-id="element-5200" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5201">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5202">
                      <UploadIcon size={16} data-id="element-5203" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-5204">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5205" />
                  </div>

                  <div data-id="element-5206">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5207">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5208" />
                      <span className="text-sm text-gray-900" data-id="element-5209">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5210">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5211">
                    Back
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5212">
                    Next
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-5213">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5214">
                  <div data-id="element-5215">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5216">
                      Other supporting documents
                    </h1>
                    <p className="text-gray-600" data-id="element-5217">
                      If you have any other document(s) that you believe is
                      valuable to assess the legitimacy of your challenge, you
                      can add it below.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5218">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5219">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5220">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5221">
                      <div className="text-gray-500" data-id="element-5222">Dispute amount</div>
                      <div className="font-medium" data-id="element-5223">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5224">
                      <div className="text-gray-500" data-id="element-5225">Response due</div>
                      <div className="font-medium" data-id="element-5226">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5227">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5228">
                  <div data-id="element-5229">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5230">
                      Provide supporting evidence
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5231">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5232" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5233" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5234">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5235">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5236">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5237">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5238" />
                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5239" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5240">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5241">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5242">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5243">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5244">
                              <EyeIcon size={20} data-id="element-5245" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5246">
                              <Trash2Icon size={20} data-id="element-5247" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5248">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5249">
                      <UploadIcon size={16} data-id="element-5250" />
                      Upload file
                    </button>
                  </div>

                  <div data-id="element-5251">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5252" />
                  </div>

                  <div data-id="element-5253">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5254">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5255" />
                      <span className="text-sm text-gray-900" data-id="element-5256">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5257">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5258">
                    Back
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5259">
                    Review
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-5260">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5261">
                  <div data-id="element-5262">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5263">
                      Review your challenge
                    </h1>
                    <p className="text-gray-600" data-id="element-5264">
                      Please confirm that the information you have provided is
                      correct before submission.
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5265">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5266">
                      Dispute ID: {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5267">
                      View details
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5268">
                      <div className="text-gray-500" data-id="element-5269">Dispute amount</div>
                      <div className="font-medium" data-id="element-5270">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5271">
                      <div className="text-gray-500" data-id="element-5272">Response due</div>
                      <div className="font-medium" data-id="element-5273">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5274">
                        Due in 1 hour
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5275">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5276">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5277">
                      Challenge details
                    </h3>
                    <div className="space-y-3" data-id="element-5278">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5279">
                        <span className="text-sm text-gray-600" data-id="element-5280">
                          Reason for challenge
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5281">
                          The customer has a purchasing history with me
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5282">
                        <span className="text-sm text-gray-600" data-id="element-5283">
                          Type of product or service
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5284">
                          Physical product
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-5285">
                          <span className="text-sm text-gray-600" data-id="element-5286">
                            Supporting evidence
                          </span>
                          <div className="space-y-2" data-id="element-5287">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-5288">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5289">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5290">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5291" />
                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5292" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-5293">
                                  <div className="text-gray-900 font-medium" data-id="element-5294">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-5295">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-5296">
                                  <EyeIcon size={16} data-id="element-5297" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5298">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5299">
                      Transaction details
                    </h3>
                    <div className="space-y-3" data-id="element-5300">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5301">
                        <span className="text-sm text-gray-600" data-id="element-5302">
                          Product details
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5303">
                          IPHONE7 * 5,{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5304">
                        <span className="text-sm text-gray-600" data-id="element-5305">
                          Shipping address
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5306">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5307">
                        <span className="text-sm text-gray-600" data-id="element-5308">
                          Customer name
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5309">
                          Test name
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5310">
                        <span className="text-sm text-gray-600" data-id="element-5311">
                          Customer email
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5312">
                          customer@example.com
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5313">
                        <span className="text-sm text-gray-600" data-id="element-5314">
                          Customer purchase IP
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5315">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5316">
                        <span className="text-sm text-gray-600" data-id="element-5317">
                          Customer billing address
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5318">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-5319">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-5320" />
                      <p className="text-sm text-gray-900" data-id="element-5321">
                        Please attach at least one of the recommended evidence
                        types to proceed with the response.
                      </p>
                    </div>}

                  <div data-id="element-5322">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5323">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5324" />
                      <span className="text-sm text-gray-900" data-id="element-5325">
                        I understand that the current evidence can only be
                        submitted once for the issuer's review, and cannot be
                        reused.
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5326">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5327">
                    Back
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5328">
                    Submit
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-5329">
                <div className="text-center mb-8" data-id="element-5330">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 mb-6" data-id="element-5331">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5332">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="2" strokeLinejoin="round" data-id="element-5333" />
                      <path d="M6 24L24 34L42 24" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5334" />
                      <path d="M6 34L24 44L42 34" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5335" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5336">
                    Your dispute challenge has been submitted.
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-5337">
                    Thank you for submitting your information. The issuing bank
                    will review your challenge and make a decision within 115
                    days. We will notify you on any updates along the process.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-5338">
                  <div data-id="element-5339">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5340">
                      Dispute challenge
                    </h2>

                    <div className="space-y-6" data-id="element-5341">
                      <div data-id="element-5342">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5343">
                          Challenge details
                        </h3>
                        <div className="space-y-3" data-id="element-5344">
                          <div className="flex" data-id="element-5345">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5346">
                              Reason for challenge
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5347">
                              The customer has a purchasing history with me
                            </span>
                          </div>
                          <div className="flex" data-id="element-5348">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5349">
                              Type of product or service
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5350">
                              Physical product
                            </span>
                          </div>
                          <div className="flex" data-id="element-5351">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5352">
                              Supporting evidence
                            </span>
                            <div className="flex-1" data-id="element-5353">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-5354">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5355">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5356">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5357" />
                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5358" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-5359">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-5360">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-5361">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-5362">
                                      <EyeIcon size={18} data-id="element-5363" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-5364">
                                  No files uploaded
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-5365">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5366">
                          Transaction details
                        </h3>
                        <div className="space-y-3" data-id="element-5367">
                          <div className="flex" data-id="element-5368">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5369">
                              Product details
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5370">
                              IPHONE7 * 5,{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5371">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5372">
                              Shipping address
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5373">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5374">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5375">
                              Customer name
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5376">
                              Test name
                            </span>
                          </div>
                          <div className="flex" data-id="element-5377">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5378">
                              Customer email
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5379">
                              customer@example.com
                            </span>
                          </div>
                          <div className="flex" data-id="element-5380">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5381">
                              Customer purchase IP
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5382">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-5383">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5384">
                              Customer billing address
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5385">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-5386">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-5387">
                      View all Disputes
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-5388">
                      Back to Dispute details
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5389">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5390">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-5391">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5392"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5393">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5394">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-5395">
                <div className="flex items-start gap-4" data-id="element-5396">
                  <div className="flex-shrink-0" data-id="element-5397">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5398">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-5399" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-5400">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5401">
                      Are you sure you want to proceed without evidence?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-5402">
                      Submitting a challenge without supporting evidence
                      significantly reduces your chances of winning the dispute.
                      We strongly recommend providing at least one type of
                      evidence to support your case.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-5403">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-5404">
                  Go back and add evidence
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-5405">
                  Proceed anyway
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}